import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, ChevronRight, Check, AlertTriangle, X, Star } from 'lucide-react';

const FLAG_STYLES = { green: { bg: 'bg-[#10b981]/10', border: 'border-[#10b981]/20', text: 'text-[#10b981]', icon: Check },
  yellow: { bg: 'bg-[#f59e0b]/10', border: 'border-[#f59e0b]/20', text: 'text-[#f59e0b]', icon: AlertTriangle },
  red: { bg: 'bg-[#ef4444]/10', border: 'border-[#ef4444]/20', text: 'text-[#ef4444]', icon: X } };

export default function BonusTermsDecoder() {
  const [bonusText, setBonusText] = useState('');
  const [casinoName, setCasinoName] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [selectedSample, setSelectedSample] = useState('');
  const [samples, setSamples] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/tools/bonus-samples').then(r => setSamples(r.data.samples)).catch(() => {}); }, []);

  const handleSampleSelect = (idx) => {
    const s = samples[parseInt(idx)];
    if (s) { setBonusText(s.bonus_text); setCasinoName(s.casino); setSelectedSample(idx); }
  };

  const handleDecode = async (e) => {
    e.preventDefault();
    if (!bonusText.trim()) { toast.error('Please enter bonus terms'); return; }
    setLoading(true);
    try {
      const res = await api.post('/tools/decode-bonus', { bonus_text: bonusText, casino_name: casinoName, bonus_amount: parseFloat(bonusAmount) || 0 });
      setResult(res.data);
    } catch (err) { toast.error('Decoding failed'); }
    finally { setLoading(false); }
  };

  const REC = { take: { label: 'TAKE THIS BONUS', color: '#10b981', icon: Check }, caution: { label: 'PROCEED WITH CAUTION', color: '#f59e0b', icon: AlertTriangle }, skip: { label: 'SKIP THIS BONUS', color: '#ef4444', icon: X } };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="bonus-decoder-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6">
        <Link to="/dashboard" className="hover:text-[#4d7cfe]">Dashboard</Link><ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Play Smart</span><ChevronRight className="h-3.5 w-3.5" /><span className="text-white">Bonus Terms Decoder</span>
      </nav>
      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#f59e0b]/15 flex items-center justify-center shrink-0"><FileText className="h-6 w-6 text-[#f59e0b]" /></div>
        <div><h1 className="text-2xl lg:text-[32px] font-bold text-white">Bonus Terms Decoder</h1><p className="text-[#a0a8b8] text-sm mt-1">Analyze casino bonus offers and decode complex terms into plain language</p></div>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="ciq-card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-sm">Quick Select</h3>
              <Select value={selectedSample} onValueChange={handleSampleSelect}>
                <SelectTrigger className="w-[200px] h-9 bg-[#1a1f2e] border-white/10 text-white text-xs" data-testid="bonus-sample-select"><SelectValue placeholder="Load sample bonus" /></SelectTrigger>
                <SelectContent className="bg-[#252b3b] border-white/10">{samples.map((s, i) => <SelectItem key={i} value={String(i)}>{s.casino}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <form onSubmit={handleDecode} className="space-y-4">
              <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Paste Bonus Terms</label>
                <textarea value={bonusText} onChange={e => setBonusText(e.target.value)} rows={4} required
                  className="w-full px-4 py-3 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm placeholder:text-[#64748b] ciq-input focus:outline-none resize-none"
                  placeholder="Paste the bonus terms text here..." data-testid="bonus-text-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Casino Name</label>
                  <input type="text" value={casinoName} onChange={e => setCasinoName(e.target.value)}
                    className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" placeholder="Optional" data-testid="bonus-casino-input" />
                </div>
                <div><label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Bonus Amount</label>
                  <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                    <input type="number" value={bonusAmount} onChange={e => setBonusAmount(e.target.value)}
                      className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none" placeholder="100" data-testid="bonus-amount-input" />
                  </div>
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-[#f59e0b] text-white font-medium text-sm btn-lift disabled:opacity-50" data-testid="bonus-decode-btn">
                {loading ? 'Analyzing...' : 'Decode Bonus Terms'}
              </button>
            </form>
          </div>
          {result && (
            <div className="animate-slide-up space-y-4">
              <div className={`ciq-card border-${result.recommendation === 'take' ? '[#10b981]' : result.recommendation === 'caution' ? '[#f59e0b]' : '[#ef4444]'}/30`}>
                <div className="flex items-center justify-between mb-2">
                  <div><p className="text-xs text-[#a0a8b8] uppercase">Overall Rating</p>
                    <p className="text-2xl font-bold text-white">{result.overall_rating} <span className="text-lg text-[#a0a8b8]">({result.score}/10)</span></p>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="badge-green px-2 py-1 rounded-full">{result.flags.green} green</span>
                    <span className="badge-yellow px-2 py-1 rounded-full">{result.flags.yellow} yellow</span>
                    <span className="px-2 py-1 rounded-full bg-[#ef4444]/15 text-[#ef4444] border border-[#ef4444]/30">{result.flags.red} red</span>
                  </div>
                </div>
                {(() => { const r = REC[result.recommendation]; return (
                  <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg" style={{ background: `${r.color}15` }}>
                    <r.icon className="h-4 w-4" style={{ color: r.color }} /><span className="text-sm font-semibold" style={{ color: r.color }}>{r.label}</span>
                  </div>
                ); })()}
              </div>
              {result.terms.map((term, i) => {
                const f = FLAG_STYLES[term.flag];
                return (
                  <div key={i} className={`ciq-card ${f.bg} border ${f.border}`} data-testid={`decoded-term-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <f.icon className={`h-4 w-4 ${f.text}`} /><h4 className="text-white font-semibold text-sm">{term.name}</h4>
                      <span className={`ml-auto text-xs font-bold ${f.text}`}>{term.value}</span>
                    </div>
                    <p className="text-sm text-[#a0a8b8] mb-1">{term.plain_english}</p>
                    <p className="text-xs text-[#64748b]">{term.insight}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <CasinoSidebar sourcePage="bonus-decoder" />
      </div>
    </div>
  );
}
